import React, { useState } from "react";

export default function GalleryLightbox({ title, description, images = [] }) {
  const [selectedIndex, setSelectedIndex] = useState(null);

  if (!images || images.length === 0) return null;

  const openLightbox = (index) => setSelectedIndex(index);
  const closeLightbox = () => setSelectedIndex(null);

  const prevImage = (e) => {
    e?.stopPropagation();
    setSelectedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextImage = (e) => {
    e?.stopPropagation();
    setSelectedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="space-y-6">
      {(title || description) && (
        <div className="space-y-2">
          {title && <h3 className="text-2xl font-bold font-heading text-[#2C2523]">{title}</h3>}
          {description && <p className="text-sm text-[#5C524C]">{description}</p>}
        </div>
      )}

      {/* Grid of gallery images */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {images.map((item, idx) => {
          const imgUrl = typeof item === "string" ? item : item.url;
          const caption = typeof item === "object" ? item.caption : "";

          return (
            <div
              key={idx}
              onClick={() => openLightbox(idx)}
              className="group relative h-64 rounded-2xl overflow-hidden border border-[#EADFCF] bg-[#FAF7F2] cursor-pointer shadow-xs hover:shadow-md transition-all duration-300"
            >
              <img
                src={imgUrl}
                alt={caption || title || `Galleri billede ${idx + 1}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C2523]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <div className="flex items-center justify-between w-full text-white text-xs">
                  <span>{caption || "Klik for at forstørre"}</span>
                  <div className="w-8 h-8 rounded-full bg-[#FAF7F2]/30 backdrop-blur-md flex items-center justify-center text-white">
                    <i className="fa-solid fa-expand" aria-hidden="true"></i>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Lightbox Modal */}
      {selectedIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-50 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer text-lg"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>

          {/* Prev button */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer text-xl"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
            </button>
          )}

          {/* Image & Caption */}
          <div
            className="max-w-5xl max-h-[85vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={typeof images[selectedIndex] === "string" ? images[selectedIndex] : images[selectedIndex].url}
              alt="Galleri forstørrelse"
              className="max-w-full max-h-[75vh] object-contain rounded-xl shadow-2xl"
            />
            {typeof images[selectedIndex] === "object" && images[selectedIndex].caption && (
              <p className="mt-4 text-sm font-medium text-white/90 bg-black/40 px-4 py-2 rounded-full backdrop-blur-md">
                {images[selectedIndex].caption}
              </p>
            )}
            <p className="mt-2 text-xs text-white/60">
              {selectedIndex + 1} af {images.length}
            </p>
          </div>

          {/* Next button */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer text-xl"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
